import { CommonModule } from '@angular/common';
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnInit,
   Output
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { AuthService } from '@services/auth.service';

interface CustomMenuItem extends MenuItem {
   customIcon?: string;
}

@Component({
   selector: 'main-side-menu',
   standalone: true,
   imports: [CommonModule, MenuModule, RouterModule, TranslateModule, TooltipModule],
   templateUrl: './main-side-menu.component.html',
   styleUrl: './main-side-menu.component.scss',
   changeDetection: ChangeDetectionStrategy.Default
})

export class MainSideMenuComponent implements OnInit {

   menuItems: CustomMenuItem[] = [];
   @Input() isCollapsed = false;
   @Input() isMobile = false;


   isDarkTheme = false;

   @Output() isCollapsedChange = new EventEmitter<boolean>();
   @Output() onClickMenu = new EventEmitter<boolean>();

   constructor(
      public router: Router,
      private cdr: ChangeDetectorRef,
      private translate: TranslateService,
      private authService: AuthService
   ) {
      this.isCollapsed = false;

      this.isDarkTheme = document.body.classList.contains('dark-theme');
   }

   ngOnInit() {
      this.initializeMenuItems();

      const savedState = localStorage.getItem('sidebarCollapsed');
      if (savedState !== null) {
         this.isCollapsed = savedState === 'true';
         this.isCollapsedChange.emit(this.isCollapsed);
      }

      this.translate.onLangChange.subscribe(() => {
         this.cdr.detectChanges();
      });

      this.router.events.subscribe(() => {
         this.initializeMenuItems();
         this.cdr.detectChanges();
      });

      const observer = new MutationObserver(() => {
         this.isDarkTheme = document.body.classList.contains('dark-theme');
         this.cdr.detectChanges();
      });

      observer.observe(document.body, {
         attributes: true,
         attributeFilter: ['class']
      });
   }

   isActiveRoute(item: MenuItem): boolean {
      if (!item.routerLink || item.routerLink.length === 0) return false;
      const url = Array.isArray(item.routerLink) ? item.routerLink[0] : item.routerLink;
      return url.length > 2 && this.router.url.startsWith(url);
   }

   toggleSidebar(): void {
      this.isCollapsed = !this.isCollapsed;
      localStorage.setItem('sidebarCollapsed', this.isCollapsed.toString());
      this.isCollapsedChange.emit(this.isCollapsed);
      this.cdr.detectChanges();
   }

   getIconPath(iconName: string): string {
      const theme = this.isDarkTheme ? 'dark' : 'light';
      return `assets/icons/main/${theme}/ico-${iconName}.svg`;
   }

   selectMenu(item: any): void {
      this.onClickMenu.emit(true);
      if (item.routerLink && item.routerLink[0]) {
         this.router.navigate(item.routerLink).then(
            success => console.log('Navigation success:', success),
            error => console.error('Navigation error:', error)
         );
      }
   }

   private initializeMenuItems(): void {
      const filteredItems = this.SideMenuItems.filter(item => {
         if (item.title === 'Admin' && !this.authService.isAdmin()) {
            return false;
         }
         if (item.title === 'Dictionaries' && this.authService.hasRole('Operator') && !this.authService.isAdmin()) {
            return false;
         }
         return true;
      });

      this.menuItems = filteredItems.map(item => ({
         label: item.title,
         icon: item.icon,
         routerLink: item.disabled ? null : [item.url],
         disabled: item.disabled,
         customIcon: item.icon
      }));
   }

   protected readonly SideMenuItems: any[] = [
      {
         icon: 'main-menu',
         title: 'Main menu',
         url: '/main-menu',
         disabled: false
      },
      {
         icon: 'crop-monitoring',
         title: 'Crop monitoring',
         url: '/crop-monitoring',
         disabled: false
      },
      {
         icon: 'soil-composition',
         title: 'main_menu.electronic_field_maps',
         url: '/electronic-field-map',
         disabled: false
      },
      {
         icon: 'map',
         title: 'GENERAL_GIS.TITLE',
         url: '/general-map',
         disabled: false
      },
      {
         icon: 'weather',
         title: 'Weather',
         url: '/weather',
         disabled: false
      },
      {
         icon: 'satellite-data',
         title: 'Satellite Data',
         url: '/satellite-data',
         disabled: false
      },
      {
         icon: 'uav-data',
         title: 'UAV data',
         url: '/uav-data',
         disabled: false
      },
      {
         icon: 'fields-identification',
         title: 'Fields identification',
         url: '/fields-identification',
         disabled: false
      },
      {
         icon: 'crop-identification',
         title: 'Crop identification',
         url: '/crop-identification',
         disabled: false
      },
      {
         icon: 'crop-yield-forecasts',
         title: 'Crop Yield Forecasts',
         url: '/crop-yield-forecasts',
         disabled: false
      },
      {
         icon: 'reports',
         title: 'reporting_analytics.title',
         url: '/reports',
         disabled: false
      },
      {
         icon: 'dictionaries',
         title: 'Dictionaries',
         url: '/dictionaries',
         disabled: false
      },
      {
         icon: 'admin',
         title: 'Administration',
         url: '/admin',
         disabled: false
      }
   ];

}
